import { taskRailRoutes } from '@modules/Tasks/Resources/js/utils/taskRailRoutes';

export default {
    slug: 'opportunities',
    ability: 'opportunities',
    // The prospect facet is declared by Contacts: it is a contact one hopes to
    // sell to, held whether or not the pipeline was bought. What lives here is
    // the pipeline.
    routes: [
        {
            path: 'opportunities',
            name: 'opportunity-board',
            component: () => import('@modules/Opportunities/Resources/js/pages/Board.vue'),
            // One request for what the board and the list under it ask for as they
            // open (utils/listBootstrap.js); the card view uses the filters and the
            // saved views, the list view all five parts.
            meta: { listBootstrap: { list: 'opportunities', object: 'opportunity' } },
            children: [
                {
                    path: 'new',
                    name: 'opportunity-create',
                    component: () => import('@modules/Opportunities/Resources/js/pages/CreatePanel.vue')
                },
                {
                    path: ':id(\\d+)',
                    name: 'opportunity-preview',
                    component: () => import('@modules/Opportunities/Resources/js/pages/Preview.vue'),
                    redirect: { name: 'opportunity-overview' },
                    props: true,
                    children: [
                        {
                            path: '/organization/:org/opportunities/:id(\\d+)',
                            name: 'opportunity-overview',
                            component: () => import('@modules/Opportunities/Resources/js/pages/preview/Overview.vue'),
                            props: true
                        },
                        {
                            path: 'activities',
                            name: 'opportunity-activities',
                            component: () => import('@/components/base/tabs/TabActivities.vue'),
                            props: true,
                            children: taskRailRoutes('opportunity-activities')
                        },
                        {
                            path: 'comments',
                            name: 'opportunity-comments',
                            component: () => import('@/components/base/tabs/TabComments.vue'),
                            props: true
                        },
                        {
                            path: 'tasks',
                            name: 'opportunity-tasks',
                            component: () => import('@/components/base/tabs/TabTasks.vue'),
                            props: true,
                            children: taskRailRoutes('opportunity-tasks')
                        },
                        {
                            path: 'children',
                            name: 'opportunity-children',
                            component: () => import('@modules/Opportunities/Resources/js/pages/preview/Children.vue'),
                            props: true
                        },
                        {
                            path: 'logs',
                            name: 'opportunity-logs',
                            component: () => import('@/components/base/tabs/TabLogs.vue'),
                            props: true
                        },
                        {
                            path: 'documents/:docPath(.*)*',
                            name: 'opportunity-documents',
                            component: () => import('@/components/base/tabs/TabDocuments.vue'),
                            props: true
                        },
                        {
                            path: 'shortcuts',
                            name: 'opportunity-shortcuts',
                            component: () => import('@/components/base/tabs/TabShortcuts.vue'),
                            props: true
                        },
                        {
                            path: 'document-requests',
                            name: 'opportunity-document-requests',
                            component: () => import('@/components/base/tabs/TabDocumentRequests.vue'),
                            props: true
                        },
                        {
                            path: 'contracts',
                            name: 'opportunity-contracts',
                            component: () => import('@/components/base/tabs/TabContracts.vue'),
                            props: true
                        },
                        {
                            path: 'related',
                            name: 'opportunity-related',
                            component: () =>
                                import('@modules/Opportunities/Resources/js/pages/preview/RelatedDocuments.vue'),
                            props: true
                        },
                        {
                            path: 'ct:tabId(\\d+)',
                            name: 'opportunity-custom-tab',
                            component: () => import('@/components/base/tabs/TabCustomLayout.vue'),
                            props: true
                        }
                    ]
                }
            ]
        },
        {
            // A sibling of the board, not a settings child: `settingsRoutes` is
            // mounted under /settings. The `report-` name prefix keeps it out of
            // NavBar's route/module map, which claims the opportunities prefix.
            path: 'reports/opportunity-forecast',
            name: 'report-opportunity-forecast',
            component: () => import('@modules/Opportunities/Resources/js/pages/reports/Forecast.vue')
        },
        {
            path: 'reports/opportunity-loss-reasons',
            name: 'report-opportunity-loss-reasons',
            component: () => import('@modules/Opportunities/Resources/js/pages/reports/LossReasons.vue')
        }
    ],
    // Tabs of the CRM settings screen, which Contacts owns (see getCrmTabs()):
    // the pipeline's settings are this module's, the screen is not.
    crmTabs: () => [
        {
            position: 10,
            title: i18n.t('General'),
            icon: 'tune',
            route: 'settings-crm-general',
            ability: 'settings.opportunities'
        },
        {
            position: 20,
            title: i18n.t('Pipelines'),
            icon: 'pipe',
            route: 'settings-pipelines',
            ability: 'settings.opportunities'
        },
        {
            position: 25,
            title: i18n.t('Opportunity types'),
            icon: 'shape-outline',
            route: 'settings-crm-opportunity-types',
            ability: 'settings.opportunities'
        }
    ],
    crmRoutes: [
        {
            path: 'general',
            name: 'settings-crm-general',
            component: () => import('@modules/Opportunities/Resources/js/pages/settings/CrmGeneralSettings.vue')
        },
        {
            path: 'pipelines',
            name: 'settings-pipelines',
            component: () => import('@modules/Opportunities/Resources/js/pages/settings/PipelineSettings.vue')
        },
        {
            path: 'opportunity-types',
            name: 'settings-crm-opportunity-types',
            component: () => import('@modules/Opportunities/Resources/js/pages/settings/OpportunityTypeSettings.vue')
        }
    ],
    // "Pipeline", not "Sales": the reports index keys its sections on the title,
    // and Accounting already publishes a "Sales" one.
    reports: () => ({
        title: i18n.t('Pipeline'),
        items: [
            {
                route: 'report-opportunity-forecast',
                icon: 'chart-timeline-variant',
                title: i18n.t('Weighted Forecast'),
                description: i18n.t('Open pipeline weighted by probability, by month, owner and pipeline')
            },
            {
                route: 'report-opportunity-loss-reasons',
                icon: 'close-octagon-outline',
                title: i18n.t('Loss reasons'),
                description: i18n.t('Lost and cancelled opportunities by reason, representative and period')
            }
        ]
    }),
    widgets: () => [
        {
            id: 'pipeline',
            label: i18n.t('Pipeline'),
            icon: 'chart-bar',
            component: () => import('@modules/Opportunities/Resources/js/pages/widgets/Pipeline.vue'),
            size: 'is-4'
        },
        {
            // The same block the board's forecast view is built from; here it
            // owns its period, offered in the widget's header.
            id: 'forecast-kpis',
            label: i18n.t('Sales forecast'),
            icon: 'target',
            component: () => import('@modules/Opportunities/Resources/js/pages/widgets/ForecastKpis.vue'),
            size: 'is-6'
        },
        // The four panels of the forecast view, each usable on its own: a sales
        // director's dashboard is rarely the same four as a rep's.
        {
            id: 'forecast-months',
            label: i18n.t('Forecast by closing month'),
            icon: 'chart-timeline-variant',
            component: () => import('@modules/Opportunities/Resources/js/pages/widgets/ForecastMonths.vue'),
            size: 'is-12'
        },
        {
            id: 'forecast-stages',
            label: i18n.t('Conversion by stage'),
            icon: 'filter-outline',
            component: () => import('@modules/Opportunities/Resources/js/pages/widgets/ForecastStages.vue'),
            size: 'is-6'
        },
        {
            id: 'forecast-reps',
            label: i18n.t('By representative'),
            icon: 'account-multiple-outline',
            component: () => import('@modules/Opportunities/Resources/js/pages/widgets/ForecastReps.vue'),
            size: 'is-6'
        },
        {
            id: 'forecast-risks',
            label: i18n.t('Forecast risks'),
            icon: 'alert-outline',
            component: () => import('@modules/Opportunities/Resources/js/pages/widgets/ForecastRisks.vue'),
            size: 'is-12'
        }
    ],
    nav: () => ({
        section: 'crm',
        items: [
            {
                title: i18n.t('Opportunities'),
                icon: 'handshake-outline',
                to: { name: 'opportunity-board' },
                position: 2
            }
        ]
    })
};
